import React, { useContext, useEffect, useState, useCallback } from 'react';
import { navigate } from 'gatsby';
import { globalHistory } from '@gatsbyjs/reach-router';
import ServiceFactory from '../services/service-factory';
import { SummaryHeader } from '../header';
import ConfirmationFooter from '../footer';
import NpafFooter from '../npaf';
import { FlowRouteProps } from '../../../flow/flow';
import { ResponseData } from '../../../../hoc/context/api';
import { RichText } from '../../../../hoc/components';
import { enrollmentContext } from '../../../../hoc/context/enrollment/enrollment-context';
import { EnrollmentLayout } from '../../../../hoc/components/layout/enrollment-layout';
import useTranslation from '../../../../../hooks/useTranslation';
import useProduct from '../../../../../hooks/useProduct';
import { ModalWithButtons } from '@nvs/leap-web-components';
import {
	EventAction,
	EventCategory,
	EventKeys,
	EventLabel,
	EventValues,
} from '../../../../../services/analytics';
import useAnalytics from '../../../../../hooks/useAnalytics';

function EnrollmentConfirmation({ location, flow }: FlowRouteProps) {
	const product = useProduct();
	const analytics = useAnalytics();
	const enrollmentCtxt = useContext(enrollmentContext);
	const { t } = useTranslation(enrollmentCtxt.performer);
	const [showModal, setShowModal] = useState(false);
	const alertUser = (e: BeforeUnloadEvent) => {
		e.preventDefault();
		e.returnValue = '';
	};

	const enrollments = enrollmentCtxt.enrollmentResponse.data.map(
		(enrollmentData: ResponseData) => {
			return {
				data: enrollmentData.card,
				state: enrollmentData.state,
				service: product.services?.find(
					(service) => service?.name === enrollmentData?.service
				),
			};
		}
	);
	enrollments.sort((a, b) => {
		return (
			product.services?.findIndex(
				(service) => service.name === a.service?.name
			) -
			product.services?.findIndex((service) => service.name === b.service?.name)
		);
	});

	const sendGAEvents = useCallback(
		(buttonLabel: string) => {
			analytics.sendEvent({
				[EventKeys.EventCategory]: EventCategory.Navigation,
				[EventKeys.EventAction]: EventAction.BrowserInteraction,
				[EventKeys.EventLabel]: buttonLabel,
				[EventKeys.PageRequestTime]: new Date().getTime(),
				[EventKeys.PageURL]: `${window.location.href}`,
				[EventKeys.Event]: EventValues.GAEvent,
			});
			return null;
		},
		[analytics]
	);

	useEffect(() => {
		const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
		const eventName = isSafari ? 'pagehide' : 'beforeunload';
		window.addEventListener(eventName, alertUser);
		return () => {
			window.removeEventListener(eventName, alertUser);
		};
	}, []);

	useEffect(() => {
		return globalHistory.listen((historyObject) => {
			if (
				historyObject.location.hash !== '#importantsafetyinformation' &&
				historyObject.action === 'POP'
			) {
				setShowModal(true);
				sendGAEvents(EventLabel.Back);
				navigate(location?.pathname!);
			}
		});
	}, [location, sendGAEvents]);

	const modalTitle = <RichText html={t('pages.confirmation.leave.title')} />;
	const cancelTranslation = 'pages.utilities.intersitial.cancel';

	return (
		<>
			<EnrollmentLayout
				label={t('pages.confirmation.label')}
				isBackButton={false}
				mlrVersion={t('pages.confirmation.mlrVersion')}
				title={
					enrollmentCtxt.isStatic(product)
						? t('pages.confirmation.header.static')
						: t('pages.confirmation.header')
				}>
				<div className="flex flex-col gap-12 lg:gap-24 w-full">
					<SummaryHeader parsedData={enrollments} />
					{enrollments?.map((service, index) => {
						return (
							<ServiceFactory
								key={`servicefactory-${index}`}
								status={service.state}
								service={service.service!}
								data={service.data}
							/>
						);
					})}
					<ConfirmationFooter />
					<NpafFooter />
				</div>
			</EnrollmentLayout>
			<ModalWithButtons
				onClose={() => {
					setShowModal(false);
					sendGAEvents(t(cancelTranslation));
				}}
				open={showModal}
				closeTitle={t('pages.close.icon')}
				primaryButton={{
					text: t('pages.confirmation.leave.agree'),
					function: () => {
						sendGAEvents(t('pages.confirmation.leave.agree'));
						flow.restart();
					},
				}}
				secondaryButton={{
					text: t(cancelTranslation),
					function: () => {
						setShowModal(false);
						sendGAEvents(t(cancelTranslation));
					},
				}}
				title={modalTitle}>
				<p>
					<RichText html={t('pages.confirmation.leave.content')} />
				</p>
				<p className="mt-4">
					<RichText html={t('pages.confirmation.leave.call')} />
				</p>
			</ModalWithButtons>
		</>
	);
}

export default EnrollmentConfirmation;
